import { Feature } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { DEFAULT_SCALE, RESOLUTION_SIZE, ScaleSpinOptions, SpinOptions } from './config';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import { convertCmToPx, convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { ILocks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import { HEIGHT, IModelOriginalSizeMap, IScaleInfo, ISizeFeature, IType, WIDTH } from './size-interface';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import i18n from '@i18next/i18n';

import { ImageRelativeResizeType, IOriginalSize } from '@src/slide/domain/ui-model/element-modeles/property/image-size/image-size.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { SlideSizeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-size/slide-size.interface';
import ImageOriginalSizeWithDPI from '@src/slide/infrastructure/module/dependent/image/original-size-with-dpi';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

const TABLE_ROW_MIN_HEIGHT = 1.25; // 表格最小行高
const TABLE_COL_MIN_WIDTH = 0.71; // 表格最小列宽
const DEFAULT_SCALE_INFO = {
    scaleHeight: DEFAULT_SCALE,
    scaleWidth: DEFAULT_SCALE,
};

const RESOLUTION_SCALE_16_9 = {
    [RESOLUTION_SIZE.RESOLUTION_640_480]: 300,
    [RESOLUTION_SIZE.RESOLUTION_720_512]: 267,
    [RESOLUTION_SIZE.RESOLUTION_800_600]: 240,
    [RESOLUTION_SIZE.RESOLUTION_1024_768]: 188,
    [RESOLUTION_SIZE.RESOLUTION_1280_1024]: 150,
};
const RESOLUTION_SCALE_4_3 = {
    [RESOLUTION_SIZE.RESOLUTION_640_480]: 225,
    [RESOLUTION_SIZE.RESOLUTION_720_512]: 200,
    [RESOLUTION_SIZE.RESOLUTION_800_600]: 180,
    [RESOLUTION_SIZE.RESOLUTION_1024_768]: 141,
    [RESOLUTION_SIZE.RESOLUTION_1280_1024]: 113,
};

@Feature()
export class SizeFeature extends BaseDrawerFeature implements ISizeFeature {
    public modelOriginalSizeMap: IModelOriginalSizeMap;
    // 缓存缩放宽高，不是get，避免高度或者宽度值变化较小时，缩放宽高值一样，导致加减按钮不生效的问题
    public scaleHeight: number;
    public scaleWidth: number;
    public isBestPlayScale: boolean;
    public resolution = RESOLUTION_SIZE.RESOLUTION_640_480;
    public shouldResetDisabled = false;

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.SIZE);
    }

    public initPropsCallback = () => {
        this.initScaleInfo();
    };

    // 锁定纵横比属性
    public get size(): ILocks | undefined {
        return (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getLock?.()
            ?.getLockInfo();
    }

    public get height(): number {
        const height = (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getFrame?.()
            ?.getHeight();
        return isNaN(height) ? NaN : convertPxToCm(height);
    }

    public get width(): number {
        const width = (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getFrame?.()
            ?.getWidth();
        return isNaN(width) ? NaN : convertPxToCm(width);
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.SIZE)
    public handleHeightChange(height: number, from: NumberInputFrom) {
        if (this.isInputOnlyChangeUI()) return;
        if (this.checkIsLocalFile()) return;
        height = this.getChangeVal(height, HEIGHT, from === NumberInputFrom.INPUT);
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getFrame?.()
            ?.updateHeight({ value: height });
        this.tdwDrawerReportNumberInput(this.tdwDrawerConstMap.elementSize, { ver6: height, ver7: 1 }, from);
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.SIZE)
    public handleWidthChange(width: number, from: NumberInputFrom) {
        if (this.isInputOnlyChangeUI()) return;
        if (this.checkIsLocalFile()) return;
        width = this.getChangeVal(width, WIDTH, from === NumberInputFrom.INPUT);
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getFrame?.()
            ?.updateWidth({ value: width });
        this.tdwDrawerReportNumberInput(this.tdwDrawerConstMap.elementSize, { ver6: width, ver7: 2 }, from);
    }

    public handleLocksChange = (value: boolean): void => {
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getLock?.()
            ?.updateLocks(Number(value));
        this.tdwDrawerReport(ActionType.SIZE_ADJUST);
    };

    public getOptions = (type: IType) => {
        const selectedModel = this.getSelectModel();
        if (selectedModel?.type !== UIModelType.TABLE) {
            return SpinOptions;
        }
        const min = this.calTableMinSize(selectedModel, type);
        return { ...SpinOptions, min };
    };

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.SIZE)
    public handleElementSizeScaleChange(value: number, type: IType, from: NumberInputFrom): void  {
        if (this.isInputOnlyChangeUI()) return;
        if (this.checkIsLocalFile()) return;
        type === HEIGHT ? this.scaleHeight = value : this.scaleWidth = value;
        const selectedModel = this.getSelectModel();
        // 多选元素按照相同比例，自身大小进行缩放
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            this.handleMultiModelSizeScaleChange(value, type, selectedModel as IMultiplyModel, from);
        } else {
            this.handleSingleModelSizeScaleChange(value, type, selectedModel as IElementBaseModel);
        }
        this.tdwDrawerReportNumberInput(
            this.tdwDrawerConstMap.elementScaleSize,
            { ver6: value, ver7: type === HEIGHT ? 1 : 2 }, from,
        );
    }

    public getElementSizeScaleOptions(type: IType) {
        const selectedModel = this.getSelectModel() as IElementBaseModel;
        const { width: originalWidth, height: originalHeight } = this.getOriginalSize(selectedModel);
        const originalVal = type === HEIGHT ? originalHeight : originalWidth;
        let { min } = ScaleSpinOptions;
        const max = convertCmToPx(SpinOptions.max) / originalVal * DEFAULT_SCALE;
        if (selectedModel?.type === UIModelType.TABLE) {
            const tableMin = convertCmToPx(this.calTableMinSize(selectedModel, type));
            min = Math.max(tableMin / originalVal * DEFAULT_SCALE, min);
        }
        return { ...ScaleSpinOptions, min, max };
    }

    /**
     * 获取原始尺寸内容。只要选中元素有图片 就取第一个图片的原始尺寸值。
     */
    public getOriginalSizeContent(): string {
        let selectedModel = this.getSelectModel();
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            const selectedModels = getAllChildModels(selectedModel as IMultiplyModel);
            selectedModel = selectedModels?.find(model => model.type === UIModelType.IMAGE
                && (model as IPictureModel).isImage());
        }
        let originalSize;
        if (selectedModel?.type === UIModelType.IMAGE) {
            const picModel = selectedModel as IPictureModel;
            const relId = picModel.sdkData?.props?.blipFill?.blip?.embed;
            originalSize = ImageOriginalSizeWithDPI.getAndUpdateImageOriginalSize(relId || '', picModel.props.src, () => {
                this.initScaleInfo();
                this.formService.$forceUpdate();
            });
        }
        this.shouldResetDisabled = !originalSize;
        if (originalSize) {
            const height = this.modifyShownValue(convertPxToCm(Number(originalSize.height)), SpinOptions.toFixed);
            const width = this.modifyShownValue(convertPxToCm(Number(originalSize.width)), SpinOptions.toFixed);
            return i18n.t('高度：{0} 厘米  宽度：{1} 厘米', { 0: height, 1: width });
        }
        return i18n.t('高度： 厘米  宽度： 厘米');
    }

    public handleBestPlayScale(value: boolean): void {
        this.isBestPlayScale = value;
        if (value) {
            this.handleResolution(this.resolution);
        }
        this.formService.$forceUpdate();
    }

    public handleResolution(resolution: string): void {
        this.resolution = resolution;
        const scale = this.getScaleForResolution(this.resolution);
        if (this.scaleHeight === scale && this.scaleWidth === scale) return;
        this.scaleHeight = scale;
        this.scaleWidth = scale;
        const picModel = this.getSelectModel() as IPictureModel;
        transaction(() => {
            this.handleSingleModelSizeScaleChange(scale, HEIGHT, picModel);
            this.handleSingleModelSizeScaleChange(scale, WIDTH, picModel);
        });
    }

    public initBestPlayScale = () => {
        this.isBestPlayScale = false;
        this.resolution = RESOLUTION_SIZE.RESOLUTION_640_480;
    };

    public getRelativeImageOriginalSize(): ImageRelativeResizeType {
        const selectedModel = this.getSelectModel();
        if (selectedModel?.type === UIModelType.IMAGE) {
            return (selectedModel as IPictureModel).getImageSize().getRelativeResize();
        }
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            return (selectedModel as IMultiplyModel).getImageSize().getRelativeResize();
        }
        return ImageRelativeResizeType.noRelative;
    }

    public handleRelativeImageOriginalSize = (value: boolean): void => {
        const selectedModel = this.getSelectModel();
        if (selectedModel?.type === UIModelType.IMAGE) {
            (selectedModel as IPictureModel).getImageSize().handleRelativeResize(value);
            this.tdwDrawerReport(FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE);
        }
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            (selectedModel as IMultiplyModel).getImageSize().handleRelativeResize(value);
            this.tdwDrawerReport(FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE);
        }
    };

    public initScaleInfo = () => {
        const { scaleHeight, scaleWidth } = this.getElementSizeScaleInfo();
        this.scaleHeight = scaleHeight;
        this.scaleWidth = scaleWidth;
    };

    /**
     * 获取变更值，主要处理多选且高度或者宽度不同的情况
     */
    public getChangeVal = (value: number, type: IType, isFromInput: boolean): number => {
        if (!this.isMultiModel() || !isNaN(this[type])) return convertCmToPx(value);
        // 当多选元素大小不同时，根据selectedModels中第一个不为table或本地文件的值的大小进行变化
        const { selectedModels } = this.getSelectModel() as IMultiplyModel;
        const firstModel = selectedModels.find(model => !(this.checkIsLocalFile(model)
            || model.type === UIModelType.TABLE));
        let cmValue = value;
        if (firstModel && !isFromInput) {
            cmValue = convertPxToCm(firstModel.props.style[type]);
            // 当多选大小不同时，-按钮时传入的value为0，+按钮时value为step
            cmValue = this.validNumber(
                value === 0 ? cmValue - SpinOptions.step : cmValue + SpinOptions.step,
                SpinOptions.max,
                SpinOptions.min,
            );
        }
        return convertCmToPx(cmValue);
    };

    /**
     * 重设图片为原始尺寸
     */
    public handleResetOriginalSize = (): void => {
        const selectedModel = this.getSelectModel();
        if (selectedModel?.type === UIModelType.IMAGE) {
            (selectedModel as IPictureModel).getImageSize().resetSize();
            this.tdwDrawerReport(FormItemName.RESET_ORIGINAL_SIZE);
        }
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            (selectedModel as IMultiplyModel).getImageSize().resetSize();
            this.tdwDrawerReport(FormItemName.RESET_ORIGINAL_SIZE);
        }
    };

    /**
     * 计算表格最小宽高，单位为cm
     */
    private calTableMinSize = (selectedModel, type: IType) => {
        const colLength = selectedModel.tableItems[0].length; // 列数
        const rowLength = selectedModel.tableItems.length; // 行数
        const tableMinWidth = TABLE_COL_MIN_WIDTH * colLength;
        const tableMinHeight = TABLE_ROW_MIN_HEIGHT * rowLength;
        return type === HEIGHT ? tableMinHeight : tableMinWidth;
    };

    /**
     * 是否为本地文件或本地图片
     */
    private checkIsLocalFile = (model?): boolean => (model ?? this.getSelectModel()).isTextAttachment?.();


    /**
     * 获取scaleHeight和scaleWidth
     */
    private getElementSizeScaleInfo = (): IScaleInfo => {
        const selectedModel = this.getSelectModel();
        if (!selectedModel || selectedModel.type === UIModelType.EMPTY) {
            return DEFAULT_SCALE_INFO;
        }
        return selectedModel?.type === UIModelType.MULTIPLY
            ? this.getMultiModelSizeScaleInfo(selectedModel as IMultiplyModel)
            : this.getSingleModelSizeScaleInfo(selectedModel as IElementBaseModel);
    };

    /**
     * 处理多个model的scale change事件
     */
    private handleMultiModelSizeScaleChange = (
        value: number, type: IType,
        selectedModel: IMultiplyModel, from: NumberInputFrom,
    ) => {
        const { selectedModels } = selectedModel;
        // 当多选元素全是图片时，图片以原始大小为基准进行变化。否则以选中时的大小为基准
        const isInitialSize = this.isMultiLocalImage(selectedModel);
        let fixedValue = value;
        if (isInitialSize) {
            fixedValue = this.fixMultiLocalImageValue(value, selectedModels, from, type);
        }
        const valueArr: number[] = [];
        selectedModels.forEach((model: IElementBaseModel) => {
            const { width: originalWidth, height: originalHeight } = this.getOriginalSize(model, isInitialSize);
            const scaledValue = this.getScaledValue(type === HEIGHT ? originalHeight : originalWidth, fixedValue);
            valueArr.push(scaledValue);
            // 当元素设置锁定纵横比时，缩放高度/宽度改变时，相应的缩放宽度/高度也要改变
            if (model.props?.locks?.noChangeAspect) {
                type === HEIGHT ? this.scaleWidth = fixedValue : this.scaleHeight = fixedValue;
            }
        });
        if (type === HEIGHT) {
            selectedModel.getFrame().updateDiffHeight({ value: valueArr });
        } else {
            selectedModel.getFrame().updateDiffWidth({ value: valueArr });
        }
    };

    /**
     * 处理单个model的scale change事件
     */
    private handleSingleModelSizeScaleChange = (value: number, type: IType, selectedModel: IElementBaseModel) => {
        const { width: originalWidth, height: originalHeight } = this.getOriginalSize(selectedModel);
        let scaledValue = this.getScaledValue(type === HEIGHT ? originalHeight : originalWidth, value);
        if (selectedModel.type === UIModelType.IMAGE) {
            // 图片的缩放宽高是图片实际大小/基准值
            const foregroundSize = (selectedModel as IPictureModel).getImageSize().getForegroundImageSize();
            const { height, width } = (selectedModel as IPictureModel).props.style;
            const { rh, rw } = foregroundSize;
            scaledValue *=  type === HEIGHT ? height / rh : width / rw;
        }
        const frame = selectedModel.getFrame?.();
        type === HEIGHT
            ? frame?.updateHeight({ value: scaledValue })
            : frame?.updateWidth({ value: scaledValue });
    };

    /**
     * 获取多选缩放信息
     */
    private getMultiModelSizeScaleInfo = (selectedModel: IMultiplyModel): IScaleInfo => {
        const { selectedModels } = selectedModel;
        const {
            scaleHeight: initialScaleHeight,
            scaleWidth: initialScaleWidth,
        } = this.getSingleModelSizeScaleInfo((selectedModels[0] as IPictureModel));
        // 多选不同缩放比例的图片，缩放值为''，其他多选情况缩放值为100
        const defaultValue = this.checkIsType(UIModelType.IMAGE, selectedModel) ? NaN : DEFAULT_SCALE;
        let finalScaleHeight = initialScaleHeight;
        let finalScaleWidth = initialScaleWidth;
        selectedModels.slice(1).forEach((model: IElementBaseModel) => {
            const { scaleHeight, scaleWidth } = this.getSingleModelSizeScaleInfo(model);
            if (initialScaleHeight !== scaleHeight) {
                finalScaleHeight = defaultValue;
            }
            if (initialScaleWidth !== scaleWidth) {
                finalScaleWidth = defaultValue;
            }
        });
        return {
            scaleHeight: finalScaleHeight, scaleWidth: finalScaleWidth,
        };
    };

    private getSingleModelSizeScaleInfo = (selectedModel: IElementBaseModel): IScaleInfo => {
        let height = selectedModel.getFrame?.()?.getHeight();
        let width = selectedModel.getFrame?.()?.getWidth();
        if (selectedModel.type === UIModelType.IMAGE) {
            // 图片的缩放宽高是图片实际大小/基准值
            const foregroundSize = (selectedModel as IPictureModel).getImageSize().getForegroundImageSize();
            height = foregroundSize.rh;
            width = foregroundSize.rw;
        }
        // 当宽度或者高度为0时，缩放宽高置灰且显示空白
        if (height === 0 || width === 0) {
            return {
                scaleHeight: NaN,
                scaleWidth: NaN,
            };
        }
        const { width: originalWidth, height: originalHeight } = this.getOriginalSize(selectedModel);
        return originalHeight === 0 || originalWidth === 0
            ? DEFAULT_SCALE_INFO
            : {
                scaleHeight: this.getScale(height / originalHeight),
                scaleWidth: this.getScale(width / originalWidth),
            };
    };

    /**
     * 获取元素初始大小
     * @param selectedModel 选中的元素
     * @param isInitialSize 是否要以原始大小为基准
     * @param isShown 是否仅供展示
     * @returns
     */
    private getOriginalSize = (
        selectedModel: IElementBaseModel, isInitialSize = true,
        isShown = false,
    ): IOriginalSize => {
        if (isInitialSize && selectedModel.type === UIModelType.IMAGE) {
            const picModel = selectedModel as IPictureModel;
            if (picModel.isImage()
                && (isShown || picModel.props.relativeResize !== ImageRelativeResizeType.noRelative)) {
                const originSize = picModel.getImageSize().getOriginalSize();
                if (!(isNaN(originSize?.width) || isNaN(originSize?.height))) {
                    return originSize;
                }
            }
        }
        const originSize = this.modelOriginalSizeMap?.get(selectedModel.key);
        return {
            height: Number(originSize?.height ?? this.height),
            width: Number(originSize?.width ?? this.width),
        };
    };

    /**
     * 多选元素是否都是本地上传的图片
     */
    private fixMultiLocalImageValue(
        value: number, selectedModels: Array<IElementBaseModel>,
        from: NumberInputFrom, type: IType,
    ): number {
        if (!(from === NumberInputFrom.ADD_BTN || from === NumberInputFrom.REDUCE_BTN)) return value;
        const scaleInfo = this.getSingleModelSizeScaleInfo(selectedModels[0]);
        const scale = type === HEIGHT ? scaleInfo.scaleHeight : scaleInfo.scaleWidth;
        const fixedValue = from === NumberInputFrom.ADD_BTN
            ? Number(scale) + ScaleSpinOptions.step
            : Number(scale) - ScaleSpinOptions.step;
        if (isNaN(fixedValue)) return value;
        type === HEIGHT ? this.scaleHeight = fixedValue : this.scaleWidth = fixedValue;
        return fixedValue;
    }

    /**
     * 多选元素是否都是本地上传的图片
     */
    private isMultiLocalImage(selectedModel: IMultiplyModel): boolean {
        const { selectedModels } = selectedModel;
        for (let index = 0; index < selectedModels.length; index++) {
            const element = selectedModels[index];
            if (!(element.type === UIModelType.IMAGE && (element as IPictureModel).isImage())) {
                return false;
            }
        }
        return true;
    }

    private getScale = scale => (isNaN(scale) ? DEFAULT_SCALE : Math.round(scale * DEFAULT_SCALE));

    private getScaledValue = (originalValue: number, scale: number): number => originalValue * scale / DEFAULT_SCALE;

    /**
     * 获取当前分辨率下的缩放比例
     */
    private getScaleForResolution = (resolution: string): number => {
        const { slideSizeType } = this.getUiDataService().presentation.props;
        const scales = slideSizeType === SlideSizeType.SLIDE_SIZE_4_3
            ? RESOLUTION_SCALE_4_3
            : RESOLUTION_SCALE_16_9;
        return scales[resolution];
    };
}
